import { useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useCallback, useMemo, useState } from "react";
import type { Option } from "@/constants/common";
import { useSnackbar } from "@/hooks/useSnackbar";
// Leaf import: the group-visit barrel loads the group-appointment barrel back and forms a cycle.
import type { TherapistSlotSearch } from "@/pages/app/provider/group-visit/hooks/use-therapist-slots";
import { GROUP_SESSION_DETAIL_QUERY_KEY } from "@/queries/group-visit/use-group-session-by-id";
import { GROUP_SESSION_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-sessions";
import type { TherapistSlot } from "@/queries/group-visit/use-therapist-slots";
import { useLocationsList } from "@/queries/use-locationList";
import { useRescheduleAppointment } from "@/queries/use-reschedule-appointment";
import { getStoredRealm } from "@/utils/auth-storage";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import {
  APPOINTMENT_MODE,
  GROUP_RESCHEDULE_LOCATION_PAGINATION,
  GROUP_RESCHEDULE_MESSAGE,
  INDIVIDUAL_APPOINTMENT_QUERY_KEYS,
} from "../group-appointment-constant";
import { GroupSessionDetail } from "../group-appointment-types";

export type UseGroupSessionReschedule = {
  isInPerson: boolean;
  canViewSlots: boolean;
  locationOptions: Option[];
  locationId: string | null;
  setLocationId: (locationId: string | null) => void;
  slotSearch: TherapistSlotSearch;
  selectedDate: Dayjs | null;
  selectDate: (date: Dayjs | null) => void;
  selectedSlot: TherapistSlot | null;
  selectSlot: (slot: TherapistSlot) => void;
  canSave: boolean;
  isSaving: boolean;
  save: () => Promise<void>;
};

/**
 * Moves every patient appointment of a group session to the picked slot through the
 * individual reschedule API, the same call the individual appointment drawer makes.
 */
export const useGroupSessionReschedule = (
  detail: GroupSessionDetail | undefined,
  onDone: () => void
): UseGroupSessionReschedule => {
  const client = useQueryClient();
  const showSnackbar = useSnackbar();
  const { mutateAsync: rescheduleAppointment } = useRescheduleAppointment();

  const [locationId, setLocationId] = useState<string | null>(
    detail?.locationId ?? null
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TherapistSlot | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isInPerson = detail?.mode === APPOINTMENT_MODE.IN_PERSON;

  const { data: locationList } = useLocationsList({
    pagination: GROUP_RESCHEDULE_LOCATION_PAGINATION,
    providerGroupId: getStoredRealm() ?? "",
  });

  const locationOptions = useMemo<Option[]>(
    () =>
      (locationList?.data?.content ?? []).map((location) => ({
        value: location?.uuid ?? "",
        label: location?.name ?? "",
      })),
    [locationList]
  );

  const slotSearch = useMemo<TherapistSlotSearch>(
    () => ({
      groupVisitId: detail?.groupVisitId,
      providerIds: [detail?.primaryTherapistId, detail?.coTherapistId].filter(
        (id): id is string => !!id
      ),
      availabilityMode: detail?.mode ?? APPOINTMENT_MODE.IN_PERSON,
      locationUuid: isInPerson ? (locationId ?? undefined) : undefined,
      duration: detail?.duration ?? undefined,
    }),
    [detail, isInPerson, locationId]
  );

  const handleLocationChange = useCallback((nextLocationId: string | null) => {
    setLocationId(nextLocationId);
    setSelectedSlot(null);
  }, []);

  const selectDate = useCallback((date: Dayjs | null) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  }, []);

  const save = useCallback(async () => {
    if (!detail || !selectedSlot) return;

    const appointmentIds = detail.patients
      .map((patient) => patient.appointmentId)
      .filter(Boolean);
    if (!appointmentIds.length) {
      showSnackbar({
        type: "error",
        message: GROUP_RESCHEDULE_MESSAGE.noAppointments,
      });
      return;
    }

    setIsSaving(true);
    let lastResponse: unknown;
    let firstError: unknown;
    try {
      // Sequential so the shared loading overlay stays up until the last call settles.
      for (const uuid of appointmentIds) {
        try {
          lastResponse = await rescheduleAppointment({
            rescheduleAppointment: {
              uuid,
              locationId: isInPerson ? (locationId ?? undefined) : undefined,
              startTime: selectedSlot.startTime,
              endTime: selectedSlot.endTime,
              room: detail.room ?? undefined,
            },
            xTENANTID: getStoredRealm() ?? undefined,
          });
        } catch (error) {
          firstError ??= error;
        }
      }

      client.invalidateQueries({ queryKey: [GROUP_SESSION_LIST_QUERY_KEY] });
      client.invalidateQueries({
        queryKey: [GROUP_SESSION_DETAIL_QUERY_KEY, detail.uuid],
      });
      INDIVIDUAL_APPOINTMENT_QUERY_KEYS.forEach((key) =>
        client.invalidateQueries({ queryKey: [key] })
      );

      if (firstError) {
        const snackbar = getErrorSnackbar(firstError);
        showSnackbar({
          ...snackbar,
          message: snackbar.message || GROUP_RESCHEDULE_MESSAGE.partialFailure,
        });
        return;
      }

      showSnackbar(getSuccessSnackbar(lastResponse));
      onDone();
    } finally {
      setIsSaving(false);
    }
  }, [
    detail,
    selectedSlot,
    isInPerson,
    locationId,
    rescheduleAppointment,
    client,
    showSnackbar,
    onDone,
  ]);

  return {
    isInPerson,
    canViewSlots: !isInPerson || !!locationId,
    locationOptions,
    locationId,
    setLocationId: handleLocationChange,
    slotSearch,
    selectedDate,
    selectDate,
    selectedSlot,
    selectSlot: setSelectedSlot,
    canSave: !!selectedSlot && !isSaving && (!isInPerson || !!locationId),
    isSaving,
    save,
  };
};
